package fr.frux.shared.domain.repository

import fr.frux.shared.domain.model.Harvest
import fr.frux.shared.domain.model.HarvestStatus
import fr.frux.shared.domain.model.HarvestTarget
import fr.frux.shared.domain.model.NearbyHarvests
import kotlinx.coroutines.flow.Flow

/**
 * Repository interface for harvest-related operations.
 *
 * Volontairement limité à ce que les écrans consomment réellement (#108) — le listing par arbre
 * ([getHarvestsForTree]) a rejoint ce contrat avec la fiche arbre qui l'utilise (#226).
 */
interface HarvestRepository {
    /**
     * Get harvest by ID.
     * Emits the fetched result — pas de cache local pour l'instant (cf. TreeRepository).
     */
    fun getHarvestById(id: Long): Flow<Result<Harvest>>

    /**
     * Récoltes à venir dans le rayon [radius] du point (#166), même patron que
     * [TreeRepository.getNearbyTrees] : `radius` en kilomètres, `total` porte le compte serveur
     * (#155) dont l'appelant déduit si le lot reçu est tronqué.
     */
    fun getNearbyHarvests(
        lat: Double,
        lon: Double,
        radius: Double = 5.0,
        limit: Int = 20,
    ): Flow<Result<NearbyHarvests>>

    /** Rejoint une récolte. */
    suspend fun joinHarvest(id: Long): Result<Harvest>

    /** Quitte une récolte (refusé côté serveur pour l'organisateur, ORGANIZER_CANNOT_LEAVE). */
    suspend fun leaveHarvest(id: Long): Result<Harvest>

    /**
     * Crée une récolte sur [target] (arbre ou lieu), planifiée à [scheduledDate] (epoch millis, #79).
     * L'organisateur est déduit côté serveur de la session — pas de paramètre ici. [hostedByOwner]
     * n'a de sens que sur un lieu dont l'appelant est propriétaire (403 `NOT_SITE_OWNER` sinon).
     */
    suspend fun createHarvest(
        target: HarvestTarget,
        scheduledDate: Long,
        description: String?,
        maxParticipants: Int? = null,
        hostedByOwner: Boolean = false,
    ): Result<Harvest>

    /**
     * Change le statut de la récolte [id] (organisateur uniquement, machine à états stricte —
     * cf. [HarvestStatus.canTransitionTo]). Sans optimisme : action rare et sérieuse (#226).
     */
    suspend fun updateHarvestStatus(
        id: Long,
        status: HarvestStatus,
    ): Result<Harvest>

    /**
     * Récoltes (tous statuts) déclarées pour l'arbre [treeId] — la fiche arbre (#226) filtre
     * elle-même sur `PLANNED`/`ONGOING`, ce contrat reste brut comme les autres lectures.
     */
    suspend fun getHarvestsForTree(treeId: Long): Result<List<Harvest>>

    /** Récoltes (tous statuts) déclarées sur le lieu [siteId], brutes comme [getHarvestsForTree]. */
    suspend fun getHarvestsForSite(siteId: Long): Result<List<Harvest>>

    /** Signale la description de la récolte (#515, ADR 0029). Aucun effet visible. */
    suspend fun reportHarvest(id: Long): Result<Unit>
}
